import { z } from 'zod'
import { isValidCPF } from '@/shared/utils/validate-cpf'
import { isValidCNPJ } from '@/shared/utils/validate-cnpj'

// Regex para validação de CPF formatado (XXX.XXX.XXX-XX)
const cpfFormattedRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/
// Regex para validação de CNPJ formatado (XX.XXX.XXX/XXXX-XX)
const cnpjFormattedRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/

/**
 * Detecta se o valor é um CPF ou CNPJ baseado no número de dígitos
 */
export function detectDocumentType(value: string): 'cpf' | 'cnpj' {
  const digits = value.replace(/\D/g, '')

  return digits.length <= 11 ? 'cpf' : 'cnpj'
}

/**
 * Valida CPF ou CNPJ baseado no formato
 */
function isValidCpfOrCnpj(value: string): boolean {
  if (!value || value === '') return true

  const isCpfFormat = cpfFormattedRegex.test(value)
  const isCnpjFormat = cnpjFormattedRegex.test(value)

  if (isCpfFormat) return isValidCPF(value)
  if (isCnpjFormat) return isValidCNPJ(value)

  return false
}

/**
 * Schema Zod para CPF ou CNPJ formatado
 * Detecta automaticamente e valida o formato correto
 */
export const cnpjCpfSchema = z
  .string()
  .min(14, 'CPF ou CNPJ inválido')
  .max(18, 'CPF ou CNPJ inválido')
  .refine(
    (val) => {
      if (!val || val === '') return true

      return cpfFormattedRegex.test(val) || cnpjFormattedRegex.test(val)
    },
    { message: 'CPF ou CNPJ deve estar formatado corretamente' },
  )
  .refine((val) => isValidCpfOrCnpj(val), {
    message: 'CPF ou CNPJ inválido',
  })

/**
 * Schema Zod para CPF ou CNPJ opcional (permite vazio)
 */
export const optionalCnpjCpfSchema = z
  .string()
  .refine(
    (val) => {
      if (!val || val === '') return true

      return (cpfFormattedRegex.test(val) || cnpjFormattedRegex.test(val)) && isValidCpfOrCnpj(val)
    },
    { message: 'CPF ou CNPJ inválido' },
  )
  .optional()
